import React, {
  useEffect,
  useState,
} from "react";

import { toast } from "react-toastify";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import ProjectApi from "../../api/projectApi";
import BugApi from "../../api/BugApi";

import {
  showError,
  showSuccess,
} from "../../utils/toastUtils";


function BugDetails() {

  const navigate =
    useNavigate();

  const {
    projectId,
  } = useParams();


  const [
    project,
    setProject,
  ] = useState(null);


  const [
    bugs,
    setBugs,
  ] = useState([]);


  const [
    loading,
    setLoading,
  ] = useState(true);


  const [
    submitting,
    setSubmitting,
  ] = useState(false);


  const [
    showForm,
    setShowForm,
  ] = useState(false);


  /* =========================================
     SELECTED FILES
  ========================================= */

  const [
    attachments,
    setAttachments,
  ] = useState([]);


  const [
    previews,
    setPreviews,
  ] = useState([]);


  const [
    formData,
    setFormData,
  ] = useState({

    title: "",

    bugType: "ui",

    severity: "low",

    priority: "low",

    description: "",

    expectedResult: "",

    actualResult: "",
  });


  /* =========================================
     FETCH PROJECT
  ========================================= */

  const fetchProject =
    async () => {

      try {

        const res =
          await ProjectApi.details(
            projectId,
          );

        if (
          res?.code === 200
        ) {

          setProject(
            res?.data?.project ||
            res?.data,
          );

        }

      } catch (error) {

        console.error(
          "PROJECT DETAILS ERROR:",
          error,
        );
      }
    };


  /* =========================================
     FETCH BUGS
  ========================================= */

  const fetchBugs =
    async () => {

      try {

        const res =
          await BugApi.getProjectBugs(
            projectId,
          );

        if (
          res?.code === 200
        ) {

          const bugData =
            res?.data?.bugs ||
            res?.data ||
            [];

          setBugs(
            Array.isArray(
              bugData,
            )
              ? bugData
              : [],
          );

        } else {

          setBugs([]);

        }

      } catch (error) {

        console.error(
          "FETCH BUGS ERROR:",
          error,
        );

        setBugs([]);
      }
    };


  /* =========================================
     FETCH ALL
  ========================================= */

  const fetchData =
    async () => {

      try {

        setLoading(true);

        await Promise.all([
          fetchProject(),
          fetchBugs(),
        ]);

      } finally {

        setLoading(false);
      }
    };


  useEffect(() => {

    if (projectId) {
      fetchData();
    }

  }, [projectId]);


  /* =========================================
     HANDLE INPUT
  ========================================= */

  const handleChange =
    (e) => {

      const {
        name,
        value,
      } = e.target;

      setFormData(
        (prev) => ({
          ...prev,
          [name]: value,
        }),
      );
    };


  /* =========================================
     HANDLE FILE SELECT
  ========================================= */

  const handleFileChange =
    (e) => {

      const selectedFiles =
        Array.from(
          e.target.files || [],
        );

      if (
        selectedFiles.length === 0
      ) {
        return;
      }


      /* =========================================
         MAX 10 FILES
      ========================================= */

      if (
        selectedFiles.length > 10
      ) {

        showError(
          "You can upload maximum 10 files",
        );

        return;
      }


      /* =========================================
         ALLOWED TYPES
      ========================================= */

      const allowedTypes = [

        "image/jpeg",

        "image/png",

        "image/jpg",

        "image/webp",

        "image/gif",

        "video/mp4",

        "video/webm",

        "video/quicktime",

        "video/ogg",

        "video/x-msvideo",

        "video/x-matroska",
      ];


      /* =========================================
         MAX FILE SIZE = 5MB
      ========================================= */

      const maxSize =
        5 * 1024 * 1024;


      for (
        const file of selectedFiles
      ) {

        if (
          !allowedTypes.includes(
            file.type,
          )
        ) {

          showError(
            `${file.name} is not a supported image/video file`,
          );

          return;
        }


        if (
          file.size > maxSize
        ) {

          showError(
            `${file.name} must be below 5MB`,
          );

          return;
        }
      }


      /* =========================================
         STORE FILES
      ========================================= */

      setAttachments(
        selectedFiles,
      );


      /* =========================================
         CREATE PREVIEWS
      ========================================= */

      const previewData =
        selectedFiles.map(
          (file) => ({

            file,

            url:
              URL.createObjectURL(
                file,
              ),

            type:
              file.type.startsWith(
                "video/",
              )
                ? "video"
                : "image",
          }),
        );


      setPreviews(
        previewData,
      );
    };


  /* =========================================
     REMOVE FILE
  ========================================= */

  const removeAttachment =
    (index) => {

      setAttachments(
        (prev) =>
          prev.filter(
            (_, i) =>
              i !== index,
          ),
      );


      setPreviews(
        (prev) =>
          prev.filter(
            (_, i) =>
              i !== index,
          ),
      );
    };


  /* =========================================
     RESET FORM
  ========================================= */

  const resetForm =
    () => {

      setFormData({

        title: "",

        bugType: "ui",

        severity: "low",

        priority: "low",

        description: "",

        expectedResult: "",

        actualResult: "",
      });


      setAttachments([]);

      setPreviews([]);
    };


  /* =========================================
     SUBMIT BUG
  ========================================= */

  const handleSubmit =
    async (e) => {

      e.preventDefault();


      if (
        !formData.title.trim()
      ) {

        showError(
          "Please enter bug title",
        );

        return;
      }


      if (
        !formData.description.trim()
      ) {

        showError(
          "Please enter bug description",
        );

        return;
      }


      if (
        !formData.expectedResult.trim()
      ) {

        showError(
          "Please enter expected result",
        );

        return;
      }


      if (
        !formData.actualResult.trim()
      ) {

        showError(
          "Please enter actual result",
        );

        return;
      }


      try {

        setSubmitting(true);


        /* =========================================
           FORM DATA
        ========================================= */

        const payload =
          new FormData();


        payload.append(
          "projectId",
          projectId,
        );


        payload.append(
          "title",
          formData.title.trim(),
        );


        payload.append(
          "bugType",
          formData.bugType,
        );


        payload.append(
          "severity",
          formData.severity,
        );


        payload.append(
          "priority",
          formData.priority,
        );


        payload.append(
          "description",
          formData.description.trim(),
        );


        payload.append(
          "expectedResult",
          formData.expectedResult.trim(),
        );


        payload.append(
          "actualResult",
          formData.actualResult.trim(),
        );


        /* =========================================
           ATTACH FILES
        ========================================= */

        attachments.forEach(
          (file) => {

            payload.append(
              "attachments",
              file,
            );

          },
        );


        console.log(
          "Submitting bug with files:",
          attachments,
        );


        const res =
          await BugApi.createBug(
            payload,
          );


        console.log(
          "CREATE BUG RESPONSE:",
          res,
        );


        if (
          res?.code === 200 ||
          res?.code === 201
        ) {

          showSuccess(
            res?.message ||
            "Bug reported successfully",
          );


          resetForm();


          setShowForm(
            false,
          );


          await fetchBugs();

        } else {

          showError(
            res?.message ||
            "Unable to create bug",
          );
        }

      } catch (error) {

        console.error(
          "CREATE BUG ERROR:",
          error,
        );


        showError(
          error?.message ||
          "Something went wrong while creating the bug",
        );

      } finally {

        setSubmitting(false);
      }
    };


  /* =========================================
     GET FILE URL
  ========================================= */

  const getFileUrl =
    (filePath) => {

      if (!filePath) {
        return "";
      }


      if (
        filePath.startsWith(
          "http://",
        ) ||
        filePath.startsWith(
          "https://",
        )
      ) {

        return filePath;
      }


      const baseUrl =
        process.env.REACT_APP_SOCKET_URL ||
        "http://localhost:5000/";


      return `${baseUrl.replace(
        /\/$/,
        "",
      )}${filePath}`;
    };


  /* =========================================
     FILE TYPE CHECK
  ========================================= */

  const isVideo =
    (filePath) => {

      return /\.(mp4|webm|mov|ogg|avi|mkv)$/i.test(
        filePath || "",
      );
    };


  /* =========================================
     LOADING
  ========================================= */

  if (loading) {

    return (

      <div className="content">

        <div className="text-center py-5">

          Loading Project...

        </div>

      </div>

    );
  }


  return (

    <div className="content">


      {/* =====================================
          HEADER
      ====================================== */}

      <div className="d-flex justify-content-between align-items-center mb-4">

        <div>

          <button
            type="button"
            className="btn btn-link p-0 mb-2 text-decoration-none"
            onClick={() =>
              navigate("/bugs")
            }
          >
            ← Back to Projects
          </button>


          <h3 className="fw-bold mb-1">

            🐞 Bug Management

          </h3>


          <p className="text-muted mb-0">

            Project:{" "}

            <strong>

              {
                project?.projectName ||
                "-"
              }

            </strong>

          </p>

        </div>


        <button
          type="button"
          className="btn btn-danger px-4"
          onClick={() => {

            if (showForm) {
              resetForm();
            }

            setShowForm(
              !showForm,
            );

          }}
        >

          {
            showForm
              ? "Cancel"
              : "+ Report Bug"
          }

        </button>

      </div>


      {/* =====================================
          BUG FORM
      ====================================== */}

      {showForm && (

        <div className="card border-0 shadow-sm mb-4">

          <div className="card-header bg-white">

            <h5 className="mb-0 fw-bold">

              Report New Bug

            </h5>

          </div>


          <div className="card-body">

            <form
              onSubmit={
                handleSubmit
              }
            >

              <div className="row">


                {/* BUG TITLE */}

                <div className="col-md-6 mb-3">

                  <label className="form-label">

                    Bug Title *

                  </label>


                  <input
                    type="text"
                    name="title"
                    value={
                      formData.title
                    }
                    onChange={
                      handleChange
                    }
                    className="form-control"
                    placeholder="Enter bug title"
                  />

                </div>


                {/* BUG TYPE */}

                <div className="col-md-6 mb-3">

                  <label className="form-label">

                    Bug Type

                  </label>


                  <select
                    name="bugType"
                    value={
                      formData.bugType
                    }
                    onChange={
                      handleChange
                    }
                    className="form-select"
                  >

                    <option value="ui">
                      UI Issue
                    </option>

                    <option value="functional">
                      Functional Issue
                    </option>

                    <option value="performance">
                      Performance Issue
                    </option>

                    <option value="other">
                      Other
                    </option>

                  </select>

                </div>


                {/* SEVERITY */}

                <div className="col-md-6 mb-3">

                  <label className="form-label">

                    Severity

                  </label>


                  <select
                    name="severity"
                    value={
                      formData.severity
                    }
                    onChange={
                      handleChange
                    }
                    className="form-select"
                  >

                    <option value="low">
                      Low
                    </option>

                    <option value="medium">
                      Medium
                    </option>

                    <option value="high">
                      High
                    </option>

                    <option value="critical">
                      Critical
                    </option>

                  </select>

                </div>


                {/* PRIORITY */}

                <div className="col-md-6 mb-3">

                  <label className="form-label">

                    Priority

                  </label>


                  <select
                    name="priority"
                    value={
                      formData.priority
                    }
                    onChange={
                      handleChange
                    }
                    className="form-select"
                  >

                    <option value="low">
                      Low
                    </option>

                    <option value="medium">
                      Medium
                    </option>

                    <option value="high">
                      High
                    </option>

                  </select>

                </div>


                {/* DESCRIPTION */}

                <div className="col-12 mb-3">

                  <label className="form-label">

                    Description *

                  </label>


                  <textarea
                    rows="4"
                    name="description"
                    value={
                      formData.description
                    }
                    onChange={
                      handleChange
                    }
                    className="form-control"
                    placeholder="Describe the issue in detail..."
                  />

                </div>


                {/* EXPECTED RESULT */}

                <div className="col-md-6 mb-3">

                  <label className="form-label">

                    Expected Result *

                  </label>


                  <textarea
                    rows="3"
                    name="expectedResult"
                    value={
                      formData.expectedResult
                    }
                    onChange={
                      handleChange
                    }
                    className="form-control"
                    placeholder="What should happen?"
                  />

                </div>


                {/* ACTUAL RESULT */}

                <div className="col-md-6 mb-3">

                  <label className="form-label">

                    Actual Result *

                  </label>


                  <textarea
                    rows="3"
                    name="actualResult"
                    value={
                      formData.actualResult
                    }
                    onChange={
                      handleChange
                    }
                    className="form-control"
                    placeholder="What actually happened?"
                  />

                </div>


                {/* =================================
                    ATTACHMENTS
                ================================= */}

                <div className="col-12 mb-4">

                  <label className="form-label fw-semibold">

                    Screenshot / Screen Recording

                  </label>


                  <div
                    className="border rounded p-4 text-center"
                    style={{
                      background:
                        "#f8f9fa",
                    }}
                  >

                    <i className="ti ti-cloud-upload fs-1 text-primary d-block mb-2" />


                    <h6 className="mb-1">

                      Upload Bug Evidence

                    </h6>


                    <p className="text-muted small mb-3">

                      Upload screenshots,
                      images or screen recordings.
                      Maximum 10 files, 5MB each.

                    </p>


                    <label
                      htmlFor="bugAttachments"
                      className="btn btn-outline-primary"
                    >

                      <i className="ti ti-paperclip me-1" />

                      Choose Files

                    </label>


                    <input
                      id="bugAttachments"
                      type="file"
                      className="d-none"
                      multiple
                      accept="image/*,video/*"
                      onChange={
                        handleFileChange
                      }
                    />

                  </div>


                  {/* =================================
                      PREVIEW
                  ================================= */}

                  {previews.length > 0 && (

                    <div className="row mt-3">

                      {previews.map(
                        (
                          preview,
                          index,
                        ) => (

                          <div
                            className="col-md-3 col-sm-6 mb-3"
                            key={`${preview.file.name}-${index}`}
                          >

                            <div className="card h-100">

                              <div
                                className="position-relative"
                                style={{
                                  height:
                                    "180px",
                                  background:
                                    "#f1f1f1",
                                }}
                              >

                                {preview.type ===
                                "video" ? (

                                  <video
                                    src={
                                      preview.url
                                    }
                                    controls
                                    className="w-100 h-100"
                                    style={{
                                      objectFit:
                                        "contain",
                                    }}
                                  />

                                ) : (

                                  <img
                                    src={
                                      preview.url
                                    }
                                    alt={
                                      preview.file.name
                                    }
                                    className="w-100 h-100"
                                    style={{
                                      objectFit:
                                        "contain",
                                    }}
                                  />

                                )}


                                <button
                                  type="button"
                                  className="btn btn-danger btn-sm position-absolute top-0 end-0 m-1"
                                  onClick={() =>
                                    removeAttachment(
                                      index,
                                    )
                                  }
                                >

                                  ×

                                </button>

                              </div>


                              <div className="card-body p-2">

                                <small
                                  className="d-block text-truncate"
                                  title={
                                    preview.file.name
                                  }
                                >

                                  {
                                    preview.file.name
                                  }

                                </small>


                                <small className="text-muted">

                                  {(
                                    preview.file.size /
                                    1024 /
                                    1024
                                  ).toFixed(2)}

                                  {" "}MB

                                </small>

                              </div>

                            </div>

                          </div>

                        ),
                      )}

                    </div>

                  )}

                </div>


                {/* SUBMIT */}

                <div className="col-12 text-end">

                  <button
                    type="submit"
                    className="btn btn-danger px-4"
                    disabled={
                      submitting
                    }
                  >

                    {submitting
                      ? "Submitting..."
                      : "Submit Bug"}

                  </button>

                </div>

              </div>

            </form>

          </div>

        </div>

      )}


      {/* =====================================
          BUG LIST
      ====================================== */}

      <div className="card border-0 shadow-sm">

        <div className="card-header bg-white d-flex justify-content-between align-items-center">

          <h5 className="mb-0 fw-bold">

            Reported Bugs

          </h5>


          <span className="badge bg-warning text-dark">

            {bugs.length} Bugs

          </span>

        </div>


        <div className="card-body">

          {bugs.length === 0 ? (

            <div className="text-center py-5 text-muted">

              <div className="fs-1 mb-3">

                🐞

              </div>


              <h5>
                No Bugs Found
              </h5>


              <p className="mb-0">

                No bugs have been reported
                for this project yet.

              </p>

            </div>

          ) : (

            <div className="table-responsive">

              <table className="table table-hover align-middle">

                <thead>

                  <tr>

                    <th>
                      Title
                    </th>

                    <th>
                      Type
                    </th>

                    <th>
                      Severity
                    </th>

                    <th>
                      Priority
                    </th>

                    <th>
                      Attachments
                    </th>

                    <th>
                      Status
                    </th>

                  </tr>

                </thead>


                <tbody>

                  {bugs.map(
                    (bug) => (

                      <tr
                        key={
                          bug._id
                        }
                      >

                        <td>

                          <strong>
                            {bug.title}
                          </strong>

                          <br />

                          <small className="text-muted">

                            {
                              bug.description
                            }

                          </small>

                        </td>


                        <td>

                          {
                            bug.bugType ||
                            "-"
                          }

                        </td>


                        <td>

                          <span className="badge bg-warning text-dark">

                            {
                              bug.severity
                            }

                          </span>

                        </td>


                        <td>

                          {
                            bug.priority ||
                            "-"
                          }

                        </td>


                        {/* ATTACHMENTS */}

                        <td>

                          {Array.isArray(
                            bug.attachments,
                          ) &&
                          bug.attachments.length >
                            0 ? (

                            <div className="d-flex gap-1 flex-wrap">

                              {bug.attachments.map(
                                (
                                  file,
                                  index,
                                ) => {

                                  const url =
                                    getFileUrl(
                                      file,
                                    );

                                  return isVideo(
                                    file,
                                  ) ? (

                                    <a
                                      key={
                                        index
                                      }
                                      href={
                                        url
                                      }
                                      target="_blank"
                                      rel="noreferrer"
                                      className="btn btn-sm btn-outline-primary"
                                    >

                                      🎥 Video

                                    </a>

                                  ) : (

                                    <a
                                      key={
                                        index
                                      }
                                      href={
                                        url
                                      }
                                      target="_blank"
                                      rel="noreferrer"
                                      className="btn btn-sm btn-outline-success"
                                    >

                                      🖼 Image

                                    </a>

                                  );

                                },
                              )}

                            </div>

                          ) : (

                            <span className="text-muted">

                              No attachment

                            </span>

                          )}

                        </td>


                        {/* STATUS */}

                        <td>

                          <span
                            className={
                              bug.status ===
                              "closed"

                                ? "badge bg-success"

                                : bug.status ===
                                  "fixed"

                                ? "badge bg-primary"

                                : "badge bg-danger"
                            }
                          >

                            {
                              bug.status
                            }

                          </span>

                        </td>

                      </tr>

                    ),
                  )}

                </tbody>

              </table>

            </div>

          )}

        </div>

      </div>

    </div>
  );
}


export default BugDetails;